"use client";

import React, { useState } from "react";
import type { CustomerLanguage } from "@/lib/customer-language";
import {
  calculateNaamMatching,
  naamMatchingReadiness,
  type NaamMatchingResponse,
  type NaamReadinessResponse,
} from "@/lib/api/matching-naam";
import { inspectNaamMatchingResult } from "@/lib/naam-result-quality";
import { useResultScrollAnchor } from "@/lib/use-result-scroll-anchor";
import { jsErrorFieldsFromUnknown, reportJsError } from "@/lib/js-error-beacon";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { matchingCopy } from "@/components/matching/matching-copy";
import {
  matchingCompletionContext,
  matchingToolKey,
  type MatchingCompletionSurface,
} from "@/components/matching/MatchingSurface";
import { NaamPersonField } from "@/components/matching/naam/NaamPersonField";
import { NaamResultPanel } from "@/components/matching/naam/NaamResultPanel";
import type { NaamCell } from "@/components/matching/naam/naam-cell";

/**
 * Name-based ("naam se") Gun Milan, as a MODE on its host page — never a new
 * route.
 *
 * The expansion freeze (rule 66) forbids a new URL, and there is no reason for
 * one: this is the same tool answering the same question from a weaker input.
 * State is local only — no query param, no hash, no router push — so a crawler
 * can never see a second address for the host page.
 *
 * `surface` is required: this component is rendered by both the Kundli
 * Matching and Gun Milan routes, and each completion must attribute to the
 * page the seeker is actually on (rule 47 #7).
 */
export function NaamMatchingMode({
  language,
  surface,
  onSwitchToBirthMode,
}: {
  language: CustomerLanguage;
  surface: MatchingCompletionSurface;
  onSwitchToBirthMode: () => void;
}) {
  const copy = matchingCopy(language);
  const [inputA, setInputA] = useState("");
  const [inputB, setInputB] = useState("");
  const [isNameA, setIsNameA] = useState(true);
  const [isNameB, setIsNameB] = useState(true);
  const [confirmedA, setConfirmedA] = useState<NaamCell | null>(null);
  const [confirmedB, setConfirmedB] = useState<NaamCell | null>(null);
  const [readiness, setReadiness] = useState<NaamReadinessResponse | null>(null);
  const [result, setResult] = useState<NaamMatchingResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The naam form is short and its result is tall, so the swap must be
  // anchored before paint like the birth-mode one (rule 58).
  useResultScrollAnchor(Boolean(result));

  function reset() {
    setInputA("");
    setInputB("");
    setConfirmedA(null);
    setConfirmedB(null);
    setReadiness(null);
    setResult(null);
    setError(null);
  }

  async function handleCheck() {
    setBusy(true);
    setError(null);
    try {
      setReadiness(
        await naamMatchingReadiness(
          { value: inputA, isName: isNameA },
          { value: inputB, isName: isNameB }
        )
      );
    } catch (caught) {
      reportNaamFailure(caught, "readiness");
      setError(copy.naamGenericError);
    } finally {
      setBusy(false);
    }
  }

  async function handleScore() {
    if (!confirmedA || !confirmedB) return;
    setBusy(true);
    setError(null);
    try {
      const response = await calculateNaamMatching(
        { moon_nakshatra: confirmedA.moon_nakshatra, pada: confirmedA.pada },
        { moon_nakshatra: confirmedB.moon_nakshatra, pada: confirmedB.pada },
        language
      );
      assertCompleteNaamResult(response, copy.naamGenericError);
      setResult(response);
      // Same governed tool as the host page, weaker input — the tool key is the
      // SURFACE's key and the MODE is a dimension. Minting a new CustomerToolKey
      // would split the host page's completion series in two. `input_mode`
      // mirrors the field name the engine already stamps on the result, so
      // browser and warehouse describe the mode the same way.
      trackCalculatorUsed({
        calculator: matchingToolKey(surface),
        ...matchingCompletionContext(surface, language, "naam_akshar"),
      });
    } catch (caught) {
      reportNaamFailure(caught, "score");
      setError(copy.naamGenericError);
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <NaamResultPanel
        result={result}
        language={language}
        onSwitchToBirthMode={onSwitchToBirthMode}
        onReset={reset}
      />
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <p className="text-body-small leading-relaxed text-ish-text-muted">{copy.naamIntro}</p>
      <NaamPersonField
        heading={copy.personA}
        language={language}
        value={inputA}
        isName={isNameA}
        readiness={readiness?.person_a ?? null}
        confirmed={confirmedA}
        onValueChange={setInputA}
        onIsNameChange={setIsNameA}
        onConfirm={setConfirmedA}
        onClear={() => setConfirmedA(null)}
      />
      <NaamPersonField
        heading={copy.personB}
        language={language}
        value={inputB}
        isName={isNameB}
        readiness={readiness?.person_b ?? null}
        confirmed={confirmedB}
        onValueChange={setInputB}
        onIsNameChange={setIsNameB}
        onConfirm={setConfirmedB}
        onClear={() => setConfirmedB(null)}
      />
      {error && (
        <p className="rounded-lg border border-error/20 bg-error/5 p-3 text-center text-body-small text-error">
          {error}
        </p>
      )}
      {confirmedA && confirmedB ? (
        <button
          type="button"
          onClick={() => void handleScore()}
          disabled={busy}
          className="btn-primary w-full px-5 py-3 text-body"
        >
          {busy ? copy.naamChecking : copy.naamCalculate}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void handleCheck()}
          disabled={busy || !inputA.trim() || !inputB.trim()}
          className="btn-primary w-full px-5 py-3 text-body"
        >
          {busy ? copy.naamChecking : copy.naamCheck}
        </button>
      )}
    </div>
  );
}

/**
 * The naam contract, NOT `assertCompleteMatchingResult`.
 *
 * That one requires a Mangal verdict and person details this mode has by
 * construction neither of; running it here would throw on every naam score.
 * This asserts the inverse — boundary present, Mangal explicitly not assessed —
 * so a crossed response shape is a failure rather than a rendered fabrication.
 */
function assertCompleteNaamResult(response: NaamMatchingResponse, fallback: string): void {
  const quality = inspectNaamMatchingResult(response);
  if (quality.complete) return;
  reportJsError({
    error_name: "NaamMatchingResultContractViolation",
    error_message: `naam matching result incomplete: ${quality.reasons.join(",")}`,
    boundary: `NaamMatchingMode.result.${quality.reasons[0] ?? "unknown"}`,
  });
  throw new Error(fallback);
}

function reportNaamFailure(error: unknown, stage: string): void {
  reportJsError({
    ...jsErrorFieldsFromUnknown(error),
    boundary: `NaamMatchingMode.${stage}_failed`,
  });
}
